import { request } from "../../utils/request";
import { getVideoMetadata } from "../../utils/videoMetadata";
import type { VideoMetadata } from "../../utils/videoMetadata";

export const getTaskListApi = (params:Task.PostTaskListPayloadType) => {
  return request<Task.GetTaskList>("/task/list", {
    method: "GET",
    params,
  });
};

export const postTaskApi = (body: Task.PostTaskPayloadType) => {
  return request<Task.GetTaskItem>("/task/submit", {
    method: "POST",
    body,
  });
};

// 删除任务接口
// 新接口：DELETE /video/task/delete  前缀：/admin-api
// params 参数：id（任务ID）
// 响应数据示例：{ "code": 0, "data": false, "msg": "" }
export const delTaskApi = (taskId: number) => {
  return request<{ code: number; data: boolean; msg: string }>(`/video/task/delete`, {
    method: "DELETE",
    params: {
      id: taskId,
    },
  });
};

// 批量删除任务接口
// 新接口：DELETE /video/task/batch-delete  前缀：/admin-api
// params 参数：ids（任务ID数组，以 ?ids=1&ids=2 形式传递）
// 响应数据示例：{ "code": 0, "data": true, "msg": "" }
export const batchDelTaskApi = (taskIds: number[]) => {
  return request<{ code: number; data: boolean; msg: string }>("/video/task/batch-delete", {
    method: "DELETE",
    params: {
      ids: taskIds,
    },
  });
};

export const putNameTaskApi = (id:number,name:string) => {
  return request<Task.GetTaskItem>(`/video/task/rename`, {
    method: "PUT",
    body: {
      id: id,
      name: name
    }
  });
};

// 修改视频的tag
export const updateVideoTagApi = (episodeId: number, tag: number) => {
  const payload: Task.UpdateVideoTagPayloadType = {
    episode_id: episodeId,
    tag: tag
  };

  return request<Task.UpdateVideoTagResponse>("/task/update", {
    method: "PUT",
    body: payload
  });
};

/**
 * 操作者提交视频进行审核
 *
 * 后端接口：/admin-api/video/task/submit-for-review
 * 请求方式：POST
 * 请求体参数：
 * {
 *   "id": 1 // 任务id
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": 3, // 这个数字就是 tag 更新后返回的值
 *   "msg": ""
 * }
 */
export const submitForReviewApi = (taskId: number) => {
  return request<Task.SubmitForReviewResponse>("/video/task/submit-for-review", {
    method: "POST",
    body: {
      id: taskId,
    },
  });
};

/**
 * 撤销视频提交（操作者撤销）
 *
 * 后端接口：/admin-api/video/task/revoke-submission
 * 请求方式：PUT
 * 请求体参数：
 * {
 *   "id": 1 // 任务id
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": true,
 *   "msg": ""
 * }
 */
export const revokeSubmissionApi = (taskId: number) => {
  return request<Task.RevokeSubmissionResponse>("/video/task/revoke-submission", {
    method: "PUT",
    body: {
      id: taskId,
    },
  });
};

/**
 * 审核通过 / 返工
 *
 * 后端接口：/admin-api/video/task/approve
 * 请求方式：PUT
 * 请求体参数：
 * {
 *   "id": 1,            // 任务id
 *   "approve": true|false // true 审核通过，false 返工
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": true,
 *   "msg": ""
 * }
 */
export const approveTaskApi = (taskId: number, approve: boolean) => {
  return request<Task.ApproveTaskResponse>("/video/task/approve", {
    method: "PUT",
    body: {
      id: taskId,
      approve,
    },
  });
};

// 第一个接口：获取预签名URL
export const getPresignedUrlApi = (body: Task.GetPresignedUrlPayload) => {
  return request<Task.GetPresignedUrlResponse>("/video/task/presigned-url", {
    method: "POST",
    body,
  });
};

// 第二个接口：上传文件到火山引擎对象存储
export const uploadFileToTosApi = async (
  file: File,
  signature: Task.PresignedUrlSignature,
  filePath: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<void> => {
  // 从credential中提取region（格式：AKLT.../20260104/cn-guangzhou/tos/request）
  const credentialParts = signature.credential.split('/');
  const region = credentialParts[2] || 'cn-guangzhou'; // 默认cn-guangzhou
  
  // 从originPolicy中提取bucket（解析JSON）
  let bucket = 'dev-1321001571'; // 默认值
  try {
    const policyObj = JSON.parse(signature.originPolicy);
    // 查找包含bucket的条件
    if (Array.isArray(policyObj.conditions)) {
      for (const condition of policyObj.conditions) {
        if (condition && typeof condition === 'object' && 'bucket' in condition) {
          bucket = condition.bucket;
          break;
        }
      }
    }
  } catch (e) {
    console.warn('解析originPolicy失败，使用默认bucket:', e);
  }

  // 构建TOS上传URL：https://{bucket}.tos-{region}.volces.com（必须使用 HTTPS，避免浏览器 Mixed Content 拦截）
  const tosUrl = `https://${bucket}.tos-${region}.volces.com`;

  // 构建FormData
  const formData = new FormData();
  formData.append('policy', signature.policy);
  formData.append('x-tos-algorithm', signature.algorithm);
  formData.append('x-tos-credential', signature.credential);
  formData.append('x-tos-date', signature.date);
  formData.append('x-tos-signature', signature.signature);
  formData.append('x-tos-acl', 'public-read');
  formData.append('key', filePath);
  formData.append('file', file);

  // 使用XMLHttpRequest支持进度回调
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // 监听上传进度
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });
    }

    // 监听请求完成
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`上传到对象存储失败: ${xhr.status} ${xhr.statusText}`));
      }
    });

    // 监听错误
    xhr.addEventListener('error', () => {
      reject(new Error('上传到对象存储失败: 网络错误'));
    });

    // 监听取消
    xhr.addEventListener('abort', () => {
      reject(new Error('上传已取消'));
    });

    // 监听取消信号
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    // 发送请求
    xhr.open('POST', tosUrl);
    xhr.send(formData);
  });
};

// 第三个接口：确认上传
export const confirmUploadApi = (body: Task.ConfirmUploadPayload) => {
  return request<Task.ConfirmUploadResponse>("/video/task/confirm-upload", {
    method: "POST",
    body,
  });
};

/**
 * 上传单个文件到火山引擎（通用函数）
 * @param seriesId 剧集ID
 * @param file 文件
 * @param uploadType 上传类型
 * @param onProgress 进度回调函数（可选）
 * @param signal 取消信号（可选）
 * @returns Promise<number> 返回任务ID
 */
export const uploadSingleFileToTos = async (
  seriesId: number,
  file: File,
  uploadType: 'video' | 'script' | 'subtitle' | 'cover',
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<number> => {
  // 1. 获取预签名URL
  const presignedResponse = await getPresignedUrlApi({
    seriesId,
    fileName: file.name,
    uploadType,
  });

  if (presignedResponse.code !== 0 || !presignedResponse.data) {
    throw new Error('获取预签名URL失败');
  }

  const signature = presignedResponse.data.signature;
  const filePath = presignedResponse.data.path;

  // 2. 上传文件到对象存储（带进度回调）
  await uploadFileToTosApi(file, signature, filePath, onProgress, signal);

  // 3. 如果是视频文件，获取视频时长和封面
  let duration: number | undefined;
  let coverMetadata: VideoMetadata | null = null;
  let coverPath: string | undefined; // 保存封面路径，用于后续确认上传
  
  if (uploadType === 'video' && file.type.startsWith('video/')) {
    try {
      coverMetadata = await getVideoMetadata(file);
      duration = coverMetadata.duration;
      if (!duration || duration <= 0) {
        throw new Error('获取视频时长失败或时长为0');
      }

      // 3.1 获取封面预签名URL
      const coverPresignedResponse = await getPresignedUrlApi({
        seriesId,
        fileName: coverMetadata.coverFileName,
        uploadType: 'cover',
      });

      if (coverPresignedResponse.code === 0 && coverPresignedResponse.data) {
        const coverSignature = coverPresignedResponse.data.signature;
        coverPath = coverPresignedResponse.data.path;

        // 3.2 将封面Blob转换为File对象
        const coverFile = new File([coverMetadata.coverBlob], coverMetadata.coverFileName, {
          type: 'image/jpeg',
        });

        // 3.3 上传封面到对象存储（先上传，但暂不确认）
        await uploadFileToTosApi(coverFile, coverSignature, coverPath);
      }
    } catch (error: any) {
      throw new Error(`获取视频元数据失败: ${error.message || '未知错误'}`);
    }
  }

  // 4. 确认上传（视频上传确认接口 - 必须先执行，且必须返回 code=0）
  const confirmPayload: Task.ConfirmUploadPayload = {
    seriesId,
    fileName: file.name,
    uploadType,
    filePath,
  };

  // 如果是视频，必须传递 duration
  if (uploadType === 'video') {
    if (!duration || duration <= 0) {
      throw new Error('视频时长不能为空且必须大于 0');
    }
    confirmPayload.duration = duration;
  }

  const confirmResponse = await confirmUploadApi(confirmPayload);

  if (confirmResponse.code !== 0 || !confirmResponse.data) {
    const errorMsg = confirmResponse.msg || '确认上传失败';
    throw new Error(errorMsg);
  }

  // 5. 视频上传确认成功后（code=0），再执行封面上传确认
  if (uploadType === 'video' && coverPath && coverMetadata) {
    try {
      const coverConfirmResponse = await confirmUploadApi({
        seriesId,
        fileName: coverMetadata.coverFileName,
        uploadType: 'cover',
        filePath: coverPath,
      });

      if (coverConfirmResponse.code !== 0) {
        console.warn('确认封面上传失败，但视频已上传成功');
      }
    } catch (error: any) {
      console.warn('封面上传确认失败，但视频已上传成功:', error);
      // 封面失败不影响视频上传，继续执行
    }
  }

  return confirmResponse.data.id;
};

/**
 * 完整的上传流程：上传视频和剧本文件到火山引擎
 * @param seriesId 剧集ID
 * @param videoFile 视频文件
 * @param scriptFile 剧本文件（可选）
 * @returns Promise<{videoTaskId: number, coverTaskId?: number, scriptTaskId?: number}> 返回任务ID
 */
export const uploadVideoAndScriptToTos = async (
  seriesId: number,
  videoFile: File,
  scriptFile?: File
): Promise<{ videoTaskId: number; coverTaskId?: number; scriptTaskId?: number }> => {
  let coverMetadata: VideoMetadata | null = null;
  let coverTaskId: number | undefined;

  // 1. 上传视频文件
  // 1.1 获取视频预签名URL
  const videoPresignedResponse = await getPresignedUrlApi({
    seriesId,
    fileName: videoFile.name,
    uploadType: 'video',
  });

  if (videoPresignedResponse.code !== 0 || !videoPresignedResponse.data) {
    throw new Error('获取视频预签名URL失败');
  }

  const videoSignature = videoPresignedResponse.data.signature;
  const videoPath = videoPresignedResponse.data.path;

  // 1.2 上传视频到对象存储
  await uploadFileToTosApi(videoFile, videoSignature, videoPath);

  // 1.3 如果是视频文件，获取封面和时长
  let coverPresignedData: { signature: Task.PresignedUrlSignature; path: string } | null = null;
  if (videoFile.type.startsWith('video/')) {
    try {
      coverMetadata = await getVideoMetadata(videoFile);
      
      // 1.4 获取封面预签名URL（先获取，但暂不上传确认）
      const coverPresignedResponse = await getPresignedUrlApi({
        seriesId,
        fileName: coverMetadata.coverFileName,
        uploadType: 'cover',
      });

      if (coverPresignedResponse.code === 0 && coverPresignedResponse.data) {
        coverPresignedData = {
          signature: coverPresignedResponse.data.signature,
          path: coverPresignedResponse.data.path,
        };

        // 1.5 将封面Blob转换为File对象
        const coverFile = new File([coverMetadata.coverBlob], coverMetadata.coverFileName, {
          type: 'image/jpeg',
        });

        // 1.6 上传封面到对象存储（先上传，但暂不确认）
        await uploadFileToTosApi(coverFile, coverPresignedData.signature, coverPresignedData.path);
      }
    } catch (error) {
      console.warn('获取或上传视频封面失败:', error);
      // 封面失败不影响视频上传，继续执行
    }
  }

  // 1.7 确认视频上传（必须先执行，且必须返回 code=0）
  // 获取视频时长（如果之前没有获取到，需要重新获取）
  let videoDuration: number | undefined;
  if (coverMetadata) {
    videoDuration = coverMetadata.duration;
  } else {
    try {
      const videoMetadata = await getVideoMetadata(videoFile);
      videoDuration = videoMetadata.duration;
    } catch (error) {
      console.warn('获取视频时长失败:', error);
      throw new Error('获取视频时长失败，无法确认上传');
    }
  }

  if (!videoDuration || videoDuration <= 0) {
    throw new Error('视频时长不能为空且必须大于 0');
  }

  const videoConfirmResponse = await confirmUploadApi({
    seriesId,
    fileName: videoFile.name,
    uploadType: 'video',
    filePath: videoPath,
    duration: videoDuration,
  });

  if (videoConfirmResponse.code !== 0 || !videoConfirmResponse.data) {
    throw new Error('确认视频上传失败');
  }

  const videoTaskId = videoConfirmResponse.data.id;

  // 1.8 视频上传确认成功后（code=0），再执行封面上传确认
  if (coverPresignedData && coverMetadata) {
    try {
      const coverConfirmResponse = await confirmUploadApi({
        seriesId,
        fileName: coverMetadata.coverFileName,
        uploadType: 'cover',
        filePath: coverPresignedData.path,
      });

      if (coverConfirmResponse.code === 0 && coverConfirmResponse.data) {
        coverTaskId = coverConfirmResponse.data.id;
      } else {
        console.warn('确认封面上传失败，但视频已上传成功');
      }
    } catch (error) {
      console.warn('确认封面上传失败，但视频已上传成功:', error);
      // 封面失败不影响视频上传，继续执行
    }
  }

  // 2. 如果有剧本文件，上传剧本
  let scriptTaskId: number | undefined;
  if (scriptFile) {
    // 2.1 获取剧本预签名URL
    const scriptPresignedResponse = await getPresignedUrlApi({
      seriesId,
      fileName: scriptFile.name,
      uploadType: 'script',
    });

    if (scriptPresignedResponse.code === 0 && scriptPresignedResponse.data) {
      const scriptSignature = scriptPresignedResponse.data.signature;
      const scriptPath = scriptPresignedResponse.data.path;

      // 2.2 上传剧本到对象存储
      await uploadFileToTosApi(scriptFile, scriptSignature, scriptPath);

      // 2.3 确认剧本上传
      const scriptConfirmResponse = await confirmUploadApi({
        seriesId,
        fileName: scriptFile.name,
        uploadType: 'script',
        filePath: scriptPath,
      });

      if (scriptConfirmResponse.code === 0 && scriptConfirmResponse.data) {
        scriptTaskId = scriptConfirmResponse.data.id;
      }
    }
  }

  return {
    videoTaskId,
    coverTaskId,
    scriptTaskId,
  };
};

// 新增：资源提交接口
export const submitResourceApi = (seriesId: number) => {
  return request<Task.SubmitResourceResponse>("/video/task/submit", {
    method: "POST",
    params: {
      seriesId,
    },
  });
};

// 新增：刷新重传任务接口
// 接口：POST /admin-api/video/task/retry
// 请求参数：id（任务ID）
// 响应数据：{ code: number; data: boolean; msg: string }
export const retryTaskApi = (taskId: number) => {
  return request<{ code: number; data: boolean; msg: string }>("/video/task/retry", {
    method: "POST",
    params: {
      id: taskId,
    },
  });
};




